import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type TBusinessRegistrationStep = "personalInfo" | "businessInfo";

/* ------------------------------ Step 1: Personal Info ------------------------------ */

export interface IPersonalInfoStep {
    name: string;
    lastName: string;
    gender: "male" | "female" | null; // رادیو "آقا هستم" / "خانم هستم"
    mobile: string;
    nationalCode: string;
}

/* ------------------------------ Step 2: Business Info ------------------------------ */

export interface IBusinessInfoStep {
    shopName: string;       // نام فروشگاه
    businessField: string;  // حوزه کاری
    phone: string;          // شماره تماس
    province: string;
    city: string;
    address: string;        // آدرس دقیق
    hasValidLicense: boolean; // چک‌باکس "دارای جواز معتبر هستم"
    licenseFileName: string;  // فعال فقط وقتی hasValidLicense=true
    licenseExpiry: string;    // اعتبار مجوز، فعال فقط وقتی hasValidLicense=true
}

/* ------------------------------- Root State -------------------------------- */

export interface IBusinessRegistrationState {
    step: TBusinessRegistrationStep;
    personalInfoStep: IPersonalInfoStep;
    businessInfoStep: IBusinessInfoStep;
}

/* -------------------------------------------------------------------------- */
/* Initial State                                                              */
/* -------------------------------------------------------------------------- */

const initialState: IBusinessRegistrationState = {
    step: "personalInfo",
    personalInfoStep: {
        name: "",
        lastName: "",
        gender: null,
        mobile: "",
        nationalCode: ""
    },
    businessInfoStep: {
        shopName: "",
        businessField: "",
        phone: "",
        province: "",
        city: "",
        address: "",
        hasValidLicense: false,
        licenseFileName: "",
        licenseExpiry: ""
    }
};

/* -------------------------------------------------------------------------- */
/* Slice                                                                      */
/* -------------------------------------------------------------------------- */

const BusinessRegistrationSlice = createSlice({
    name: "businessRegistration",
    initialState,
    reducers: {
        // ----- Navigation -----
        setStep: (state, action: PayloadAction<TBusinessRegistrationStep>) => {
            state.step = action.payload;
        },

        // ----- Step 1: Personal Info -----
        setPersonalInfo: (
            state,
            action: PayloadAction<Partial<Omit<IPersonalInfoStep, "gender">>>
        ) => {
            state.personalInfoStep = { ...state.personalInfoStep, ...action.payload };
        },
        setGender: (state, action: PayloadAction<IPersonalInfoStep["gender"]>) => {
            state.personalInfoStep.gender = action.payload;
        },

        // ----- Step 2: Business Info -----
        setBusinessInfo: (
            state,
            action: PayloadAction<
                Partial<Omit<IBusinessInfoStep, "hasValidLicense" | "licenseFileName" | "licenseExpiry">>
            >
        ) => {
            state.businessInfoStep = { ...state.businessInfoStep, ...action.payload };
        },
        setHasValidLicense: (state, action: PayloadAction<boolean>) => {
            state.businessInfoStep.hasValidLicense = action.payload;
            // اگر چک‌باکس غیرفعال شد، فیلدهای وابسته (فایل و اعتبار مجوز) پاک بشن
            if (!action.payload) {
                state.businessInfoStep.licenseFileName = "";
                state.businessInfoStep.licenseExpiry = "";
            }
        },
        setLicenseFileName: (state, action: PayloadAction<string>) => {
            state.businessInfoStep.licenseFileName = action.payload;
        },
        setLicenseExpiry: (state, action: PayloadAction<string>) => {
            state.businessInfoStep.licenseExpiry = action.payload;
        },

        // ----- Reset -----
        resetBusinessRegistrationForm: () => initialState
    }
});

export const {
    setStep,
    setPersonalInfo,
    setGender,
    setBusinessInfo,
    setHasValidLicense,
    setLicenseFileName,
    setLicenseExpiry,
    resetBusinessRegistrationForm
} = BusinessRegistrationSlice.actions;

export default BusinessRegistrationSlice.reducer;